import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { isEmpty } from 'lodash';
import { Button, Divider, Form, Modal, Space } from 'antd';

import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';

import { PageProps } from '@/types/PageProps';

import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import Table, { ColumnsType } from '@/components/_common/Table';

import {
  useCreateSupplierAddress,
  useDeleteSupplierAddress,
  useSupplierAddresses,
} from '@/core/domain/people/SupplierAddress/SupplierAddress.hooks';
import { useUpdateSupplierAddress } from '../../../../../core/domain/people/SupplierAddress/SupplierAddress.hooks';
import {
  CreateSupplierAddressRequest,
  SupplierAddress,
  UpdateSupplierAddressRequest,
} from '@/core/domain/people/SupplierAddress/SupplierAddress.types';
import { applyMask } from '@/utils/mask';
import { cepMask } from '@/utils/mask/commonMasks';
import { AddressModal, SaveButton } from '@/components/_common';
import { AddressDefaultType } from '@/components/_common/AddressModal/types';
import { getTableScrollX } from '@/utils/table';
import { useDisclosure } from '@/hooks';
import EditIcon from '@/components/_common/EditIcon';
import DeleteIcon from '@/components/_common/DeleteIcon';
import { useSupplierFormContext } from '@/components/Suppliers/SupplierFormProvider';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import SupplierFooter from '@/components/Suppliers/SupplierFooter';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import supplierValidationTabs from '@/utils/functions/supplierValidationTabs';

const SupplierAddressesPage = ({ route }: PageProps) => {
  const intl = useIntl();

  const [form] = Form.useForm();
  const { isLoading, supplierId, disabled, handleSubmit } =
    useSupplierFormContext();

  const { supplier, loadingDetail } = useSupplierFormContext();

  const isSupplierGlobal = !supplier?.matrixId;

  const { setParamLabel } = useBreadcrumbs();

  const [modalVisible, modalVisibleAction] = useDisclosure();
  const [selectedAddress, setSelectedAddress] = useState<SupplierAddress>();

  useEffect(() => {
    if (supplier && !isEmpty(supplier)) {
      setParamLabel(':supplierId', supplier.name);
    }
  }, [supplier, loadingDetail]);

  const {
    data: addressesData,
    isFetching,
    refetch: refetchSupplierAddress,
  } = useSupplierAddresses(+supplierId);

  const createAddressMutation = useCreateSupplierAddress(+supplierId);
  const updateAddressMutation = useUpdateSupplierAddress(+supplierId);
  const deleteAddressMutation = useDeleteSupplierAddress(+supplierId);

  const handleModalVisible = async (address?: SupplierAddress) => {
    setSelectedAddress(address ? address : undefined);

    modalVisibleAction.toggle();
  };

  const handleModalOkClick = async (values: AddressDefaultType) => {
    if (values?.id) {
      const obj: UpdateSupplierAddressRequest = {
        ...values,
      };

      await updateAddressMutation.mutateAsync(obj);
    } else {
      const obj: CreateSupplierAddressRequest = {
        ...values,
      };

      await createAddressMutation.mutateAsync(obj);
    }

    await refetchSupplierAddress();
  };

  const handleRemoveClick = (address: SupplierAddress) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'suppliers.address.remove',
      }),
      content: `${intl.formatMessage({
        id: 'suppliers.address.remove.alert',
      })} ${address.street}?`,

      onOk: deleteAddressMutation.mutateAsync,

      okType: 'danger',
      okText: intl.formatMessage({ id: 'commons.entity.remove' }),
    });
  };

  const columns: ColumnsType<SupplierAddress> = [
    {
      dataIndex: 'zipCode',
      title: intl.formatMessage({ id: 'suppliers.column.address.zipCode' }),
      render: (value) => applyMask(value, cepMask),
    },
    {
      dataIndex: 'street',
      title: intl.formatMessage({ id: 'suppliers.column.address.street' }),
      render: (_, row) => `${row.street}, ${row.number} - ${row.district}`,
    },
    {
      dataIndex: 'complement',

      title: intl.formatMessage({ id: 'suppliers.column.address.complement' }),
    },
    {
      title: intl.formatMessage({ id: 'suppliers.list.header.actions' }),
      align: 'right',
      fixed: 'right',
      render: (row) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () => handleModalVisible(row),
            }}
            disabled={!hasPermissions([Authorities.REGISTER_SUPPLIER_EDIT])}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleRemoveClick(row)}
            disabled={
              isSupplierGlobal ||
              !hasPermissions([Authorities.REGISTER_SUPPLIER_EDIT])
            }
          />
        </Space>
      ),
    },
  ];

  return (
    <Form form={form} name="supplierGeneral">
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <DefaultRouteTabs
        permissions={route.permissions}
        route={supplierValidationTabs(route, supplier)}
        baseKey={`${supplierId}`}
        basePath={`/app/registry/suppliers/${supplierId}`}
        extra={
          <SpaceForButton>
            <Button
              block
              disabled={
                !!addressesData ||
                !hasPermissions([Authorities.REGISTER_SUPPLIER_EDIT]) ||
                !supplier?.matrixId
              }
              type="primary"
              onClick={() => handleModalVisible()}
            >
              {intl.formatMessage({ id: 'commons.entity.add' })}
            </Button>

            <SaveButton
              block
              loading={isLoading}
              onClick={handleSubmit}
              disabled={
                disabled ||
                !hasPermissions([Authorities.REGISTER_SUPPLIER_EDIT])
              }
            >
              Salvar
            </SaveButton>
          </SpaceForButton>
        }
      >
        <Table<SupplierAddress>
          columns={columns}
          dataSource={addressesData?.data || []}
          loading={isFetching}
          rowKey={(row) => row.id}
          scroll={{ x: getTableScrollX(columns) }}
        />

        <SupplierFooter disabled={disabled} />

        <AddressModal
          simpleForm
          visible={modalVisible}
          onClose={handleModalVisible}
          onOk={handleModalOkClick}
          address={selectedAddress}
          fieldsDisabled={!supplier?.matrixId}
          loading={
            createAddressMutation.isLoading || updateAddressMutation.isLoading
          }
          okText={intl.formatMessage({
            id: 'supplier.modal.button.saveAddress',
          })}
        />
      </DefaultRouteTabs>
    </Form>
  );
};

export default SupplierAddressesPage;
